/**
 * PostgreSQL Script Provider
 *
 * Implements SQL script generation for PostgreSQL.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
  DatabaseScriptContext,
  SchemaScriptType,
  SchemaScriptContext,
  ColumnMetadata,
  NewObjectTemplateContext,
} from '../types/scriptProvider';
import { defaultValuesInsert } from '../../../utils/defaultValuesInsert';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';
import { appendIndexStatements } from './indexStatements';
import { resolvePrimaryKey } from './primaryKeyClause';

/**
 * Map PostgreSQL data types to their script representation
 */
function formatColumnDataType(col: ColumnMetadata): string {
  const { dataType, maxLength, precision, scale, isIdentity } = col;
  const lowerType = dataType.toLowerCase();

  // Handle serial types (auto-increment)
  if (isIdentity) {
    if (lowerType === 'integer' || lowerType === 'int4') {
      return 'SERIAL';
    }
    if (lowerType === 'bigint' || lowerType === 'int8') {
      return 'BIGSERIAL';
    }
    if (lowerType === 'smallint' || lowerType === 'int2') {
      return 'SMALLSERIAL';
    }
  }

  // Types with length
  if (['character varying', 'varchar'].includes(lowerType)) {
    return maxLength ? `VARCHAR(${maxLength})` : 'VARCHAR';
  }
  if (['character', 'char'].includes(lowerType)) {
    return maxLength ? `CHAR(${maxLength})` : 'CHAR(1)';
  }

  // Types with precision and scale
  if (['numeric', 'decimal'].includes(lowerType)) {
    if (precision !== null && scale !== null) {
      return `NUMERIC(${precision}, ${scale})`;
    }
    if (precision !== null) {
      return `NUMERIC(${precision})`;
    }
    return 'NUMERIC';
  }

  // Timestamp types
  if (lowerType.includes('timestamp')) {
    if (lowerType.includes('with time zone') || lowerType === 'timestamptz') {
      return 'TIMESTAMPTZ';
    }
    return 'TIMESTAMP';
  }

  // Map common PostgreSQL types
  const typeMap: Record<string, string> = {
    'int4': 'INTEGER',
    'int8': 'BIGINT',
    'int2': 'SMALLINT',
    'float4': 'REAL',
    'float8': 'DOUBLE PRECISION',
    'bool': 'BOOLEAN',
  };

  return typeMap[lowerType] || dataType.toUpperCase();
}

/**
 * Generate CREATE TABLE script from metadata
 */
function generateCreateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    // No metadata - generate placeholder
    return `-- CREATE TABLE script for ${fullName}
-- Run TASK-045 to fetch table metadata for complete DDL generation
CREATE TABLE ${fullName} (
    -- Column definitions would appear here
);
`;
  }

  const lines: string[] = [];
  // `UNLOGGED` is part of the definition (`ALTER TABLE … SET LOGGED` changes it),
  // so the baseline spells it the way the Table Designer's planner reads it.
  lines.push(`CREATE ${metadata.engineOptions?.unlogged ? 'UNLOGGED ' : ''}TABLE ${fullName} (`);

  // Column definitions
  const columnDefs: string[] = [];

  for (const col of metadata.columns) {
    let colDef = `    "${col.name}" ${formatColumnDataType(col)}`;

    // Don't add NULL/NOT NULL for serial types
    if (!col.isIdentity) {
      colDef += col.isNullable ? ' NULL' : ' NOT NULL';
    }

    if (col.defaultValue && !col.isIdentity) {
      colDef += ` DEFAULT ${col.defaultValue}`;
    }

    columnDefs.push(colDef);
  }

  lines.push(columnDefs.join(',\n'));

  // Primary key constraint — the catalog's name and column order when the
  // metadata carries the constraint (see `resolvePrimaryKey`).
  const primaryKey = resolvePrimaryKey(metadata, `pk_${tableName.toLowerCase()}`);
  if (primaryKey) {
    const pkCols = primaryKey.columns.map(c => `"${c}"`).join(', ');
    lines.push(`,    CONSTRAINT "${primaryKey.name.replace(/"/g, '""')}" PRIMARY KEY (${pkCols})`);
  }

  lines.push(');');

  // The table's indexes follow as CREATE INDEX statements — the Designer's
  // Indexes tab reads them from the buffer (see `indexStatements.ts`).
  return appendIndexStatements(lines.join('\n'), metadata, {
    quote: name => `"${name.replace(/"/g, '""')}"`, table: fullName,
  });
}

/**
 * Generate INSERT template script
 */
function generateInsertScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `INSERT INTO ${fullName} (column1, column2, ...)\nVALUES (value1, value2, ...);`;
  }

  // Filter out serial/identity columns for INSERT
  const insertColumns = metadata.columns.filter(c => !c.isIdentity);
  // D5 — every column is identity/serial: `INSERT INTO t () VALUES ();` is a
  // syntax error in PostgreSQL, so let the engine supply the whole row.
  if (insertColumns.length === 0) {
    return defaultValuesInsert({ engineId: 'pgsql', tableName: fullName });
  }
  const columnNames = insertColumns.map(c => `"${c.name}"`).join(', ');
  const valuePlaceholders = insertColumns.map(c => {
    // Provide type-appropriate placeholders
    const lower = c.dataType.toLowerCase();
    if (['integer', 'bigint', 'smallint', 'int4', 'int8', 'int2', 'numeric', 'decimal', 'real', 'double precision', 'float4', 'float8'].some(t => lower.includes(t))) {
      return '0';
    }
    if (lower.includes('bool')) {
      return 'FALSE';
    }
    if (lower.includes('timestamp') || lower.includes('date')) {
      return 'NOW()';
    }
    if (lower.includes('time')) {
      return 'CURRENT_TIME';
    }
    return `''`;
  }).join(', ');

  return `INSERT INTO ${fullName} (${columnNames})\nVALUES (${valuePlaceholders});`;
}

/**
 * Generate UPDATE template script
 */
function generateUpdateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `UPDATE ${fullName}\nSET column1 = value1,\n    column2 = value2\nWHERE <search_condition>;`;
  }

  // Find primary key columns for WHERE clause
  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const updateColumns = metadata.columns.filter(c => !c.isPrimaryKey && !c.isIdentity);

  const setClauses = updateColumns.slice(0, 3).map(c => `    "${c.name}" = <value>`).join(',\n');
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `"${c.name}" = <value>`).join(' AND ')
    : '<search_condition>';

  return `UPDATE ${fullName}\nSET\n${setClauses}\nWHERE ${whereClause};`;
}

/**
 * Generate DELETE template script
 */
function generateDeleteScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `DELETE FROM ${fullName}\nWHERE <search_condition>;`;
  }

  // Find primary key columns for WHERE clause
  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `"${c.name}" = <value>`).join(' AND ')
    : '<search_condition>';

  return `DELETE FROM ${fullName}\nWHERE ${whereClause};`;
}

export const pgsqlScriptProvider: ScriptProvider = {
  engine: 'pgsql',

  quoteIdentifier(name: string): string {
    return `"${name}"`;
  },

  qualifyTableName(schema: string, table: string): string {
    return `"${schema}"."${table}"`;
  },

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    const fullName = this.qualifyTableName(ctx.schema, ctx.tableName);

    switch (type) {
      case 'create':
        return generateCreateScript(ctx);

      case 'drop':
        return `DROP TABLE IF EXISTS ${fullName};`;

      case 'select':
        return `SELECT *\nFROM ${fullName};`;

      case 'select-top':
        return `SELECT *\nFROM ${fullName}\nLIMIT ${SELECT_TOP_ROW_LIMIT};`;

      case 'insert':
        return generateInsertScript(ctx);

      case 'update':
        return generateUpdateScript(ctx);

      case 'delete':
        return generateDeleteScript(ctx);

      default:
        throw new Error(`Unknown table script type: ${type}`);
    }
  },

  generateDatabaseScript(type: DatabaseScriptType, ctx: DatabaseScriptContext): string {
    const { databaseName, metadata } = ctx;
    const quotedName = `"${databaseName}"`;

    switch (type) {
      case 'create': {
        const options: string[] = [];
        if (metadata?.owner) {
          options.push(`OWNER = ${metadata.owner}`);
        }
        if (metadata?.encoding) {
          options.push(`ENCODING = '${metadata.encoding}'`);
        }
        const withClause = options.length > 0 ? `\n    WITH ${options.join('\n    ')}` : '';
        return `CREATE DATABASE ${quotedName}${withClause};`;
      }

      case 'drop':
        return `-- Warning: This will permanently delete the database and all its data
-- First, terminate all connections to the database
SELECT pg_terminate_backend(pid)
FROM pg_stat_activity
WHERE datname = '${databaseName}' AND pid <> pg_backend_pid();

DROP DATABASE IF EXISTS ${quotedName};`;

      case 'use':
        // PostgreSQL uses \\c command in psql, but in a query context we just comment
        return `-- PostgreSQL: Use \\c ${databaseName} in psql
-- Or reconnect with database: ${databaseName}`;

      default:
        throw new Error(`Unknown database script type: ${type}`);
    }
  },

  generateSchemaScript(type: SchemaScriptType, ctx: SchemaScriptContext): string {
    const quotedSchema = `"${ctx.schema}"`;
    switch (type) {
      case 'create':
        return `CREATE SCHEMA ${quotedSchema};`;
      case 'drop':
        return `DROP SCHEMA ${quotedSchema};`;
      default:
        throw new Error(`Unknown schema script type: ${type}`);
    }
  },

  // ============================================
  // "Script New" Templates with VS Code tabstops
  // ============================================

  generateNewTableTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new table in ${database}.${schema}
CREATE TABLE "${schema}"."\${1:table_name}" (
    \${2:id} SERIAL PRIMARY KEY,
    "\${3:column1}" \${4:VARCHAR(255)},
    created_at TIMESTAMPTZ DEFAULT CURRENT_TIMESTAMP
);\$0`;
  },

  generateNewViewTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new view in ${database}.${schema}
CREATE VIEW "${schema}"."\${1:view_name}"
AS
SELECT
    \${2:*}
FROM
    "${schema}"."\${3:source_table}"
WHERE
    \${4:TRUE};\$0`;
  },

  generateNewProcedureTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new stored procedure in ${database}.${schema}
CREATE OR REPLACE PROCEDURE "${schema}"."\${1:procedure_name}"(
    \${2:param1} \${3:INTEGER},
    \${4:param2} \${5:TEXT} DEFAULT NULL
)
LANGUAGE plpgsql
AS $$
BEGIN
    \${6:-- Procedure logic here}
END;
$$;\$0`;
  },

  generateNewFunctionTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new function in ${database}.${schema}
CREATE OR REPLACE FUNCTION "${schema}"."\${1:function_name}"(
    \${2:param1} \${3:INTEGER}
)
RETURNS \${4:INTEGER}
LANGUAGE plpgsql
AS $$
DECLARE
    result \${4:INTEGER};
BEGIN
    \${5:-- Function logic here}
    RETURN result;
END;
$$;\$0`;
  },

  generateNewDatabaseTemplate(): string {
    return `-- Create new database
CREATE DATABASE "\${1:database_name}"
    WITH
    OWNER = \${2:postgres}
    ENCODING = '\${3:UTF8}'
    LC_COLLATE = 'en_US.UTF-8'
    LC_CTYPE = 'en_US.UTF-8'
    TEMPLATE = template0;\$0`;
  },
};
